/**
 * Wrapper around `fetch` for BFF-bound calls.
 *
 * - Always sends cookies (`credentials: 'include'`).
 * - On state-changing methods (POST/PUT/PATCH/DELETE), reads the BFF-issued
 *   `vera.csrf` cookie and echoes its value in the `X-CSRF-Token` header.
 *   The BFF rejects the request with 403 if the header is missing or
 *   doesn't match. See `packages/bff/src/csrf.ts`.
 * - GET/HEAD/OPTIONS go through unchanged (CSRF token not required).
 */

const CSRF_COOKIE_NAME = 'vera.csrf';
const CSRF_HEADER_NAME = 'X-CSRF-Token';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const prefix = `${name}=`;
  for (const raw of document.cookie.split(';')) {
    const cookie = raw.trim();
    if (cookie.startsWith(prefix)) {
      return decodeURIComponent(cookie.slice(prefix.length));
    }
  }
  return undefined;
}

export async function bffFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);

  if (!SAFE_METHODS.has(method)) {
    const token = readCookie(CSRF_COOKIE_NAME);
    if (token && !headers.has(CSRF_HEADER_NAME)) {
      headers.set(CSRF_HEADER_NAME, token);
    }
  }

  return fetch(input, {
    ...init,
    headers,
    credentials: init.credentials ?? 'include',
  });
}
